import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { BusinessesTab } from './businesses/BusinessesTab';
import { BlogPostsTab } from './blog-posts/BlogPostsTab';
import { HomePageTab } from './home-page/HomePageTab';
import { MonitoringTab } from './monitoring/MonitoringTab';
import { EventsTab } from './events/EventsTab';
import { DealsTab } from './deals/DealsTab';
import { BusinessRecoveryTab } from './business-recovery/BusinessRecoveryTab';
import { BusinessPushNotificationsTab } from './business-push-notifications/BusinessPushNotificationsTab';
import AreaNotificationsPage from './area-notifications';
import { QrCodesTab } from './qr-codes/QrCodesTab';
import { GamificationTab } from './gamification/GamificationTab';
import { UsersTab } from './users/UsersTab';
import { NotificationOpensTab } from './notification-opens/NotificationOpensTab';
import { ReferralCodesTab } from './referral-codes/ReferralCodesTab';
import { VenueClaimsTab } from './venue-claims/VenueClaimsTab';
import { EnterprisePricingTab } from './enterprise-pricing/EnterprisePricingTab';
import { CreateHub, CreateIntent } from "./create/CreateHub";

// ─────────────────────────────────────────────────────────────────────────────
// Types

type Tab = 'create' | 'businesses' | 'users' | 'notification-opens' | 'events' | 'deals' | 'blog-posts' | 'home-page' | 'monitoring' | 'business-recovery' | 'business-push-notifications' | 'area-notifications' | 'qr-codes' | 'gamification' | 'referral-codes' | 'venue-claims' | 'enterprise-pricing';

const TAB_IDS: Tab[] = ['create', 'businesses', 'users', 'notification-opens', 'events', 'deals', 'blog-posts', 'home-page', 'monitoring', 'business-recovery', 'business-push-notifications', 'area-notifications', 'qr-codes', 'gamification', 'referral-codes', 'venue-claims', 'enterprise-pricing'];

const isTab = (value: string | null): value is Tab => !!value && (TAB_IDS as string[]).includes(value);

type TabItem = { id: Tab; label: string; icon: React.ReactNode };
type TabGroup = { kind: 'group'; id: string; label: string; icon: React.ReactNode; children: TabItem[] };
type TabEntry = TabItem | TabGroup;

const isGroup = (t: TabEntry): t is TabGroup => 'kind' in t && t.kind === 'group';

const TABS: TabEntry[] = [
  {
    id: 'create',
    label: 'Create',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>,
  },
  {
    id: 'businesses',
    label: 'Businesses',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>,
  },
  {
    id: 'users',
    label: 'Users',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>,
  },
  {
    id: 'business-recovery',
    label: 'Business Recovery',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>,
  },
  {
    kind: 'group',
    id: 'notifications-group',
    label: 'Notifications',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>,
    children: [
      {
        id: 'business-push-notifications' as Tab,
        label: 'Business Push Notifications',
        icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>,
      },
      {
        id: 'notification-opens' as Tab,
        label: 'Open Rates',
        icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      },
      {
        id: 'area-notifications' as Tab,
        label: 'Area Notifications',
        icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>,
      },
    ],
  },
  {
    kind: 'group',
    id: 'org-accounts-group',
    label: 'Org Accounts',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>,
    children: [
      {
        id: 'venue-claims' as Tab,
        label: 'Venue Claims',
        icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      },
      {
        id: 'enterprise-pricing' as Tab,
        label: 'Enterprise Pricing',
        icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      },
    ],
  },
  {
    id: 'events' as Tab,
    label: 'Events',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>,
  },
  {
    id: 'deals' as Tab,
    label: 'Deals',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" /></svg>,
  },
  {
    id: 'blog-posts',
    label: 'Blog Posts',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-4.5 5.25h4.5M12 3.75L3.75 12H6v8.25h4.5V15h3v5.25H18V12h2.25L12 3.75z" /></svg>,
  },
  {
    id: 'home-page',
    label: 'Home Page',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>,
  },
  {
    id: 'gamification',
    label: 'Gamification',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" /></svg>,
  },
  {
    id: 'referral-codes',
    label: 'Referral Codes',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>,
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>,
  },
  {
    id: 'qr-codes',
    label: 'QR Codes',
    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.008v.008H6.75V6.75zM6.75 16.5h.008v.008H6.75V16.5zM16.5 6.75h.008v.008H16.5V6.75zM13.5 13.5h.008v.008H13.5V13.5zM13.5 19.5h.008v.008H13.5V19.5zM19.5 13.5h.008v.008H19.5V13.5zM19.5 19.5h.008v.008H19.5V19.5zM16.5 16.5h.008v.008H16.5V16.5z" /></svg>,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

function BusinessesPage(): React.ReactElement {
  const isAdmin = useIsAdmin();
  const [searchParams] = useSearchParams();
  // `?tab=` deep-links a section, so other pages can point at one (the home page
  // and the header's Analytics menu do). The tab strip owns the tab between
  // navigations; a new navigation here, even to the same URL, re-applies it.
  const requestedTab = searchParams.get('tab');
  const navigationKey = useLocation().key;
  const [activeTab, setActiveTab] = useState<Tab>(isTab(requestedTab) ? requestedTab : 'businesses');
  useEffect(() => {
    if (isTab(requestedTab)) setActiveTab(requestedTab);
  }, [requestedTab, navigationKey]);
  const [createIntent, setCreateIntent] = useState<CreateIntent | null>(null);
  const openCreate = (intent: CreateIntent) => {
    setCreateIntent(intent);
    setActiveTab('create');
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  // Anchor coords (viewport-relative) for the open group's dropdown. We use
  // position:fixed so the menu escapes the tab strip's overflow-x-auto clip.
  const [openGroupAnchor, setOpenGroupAnchor] = useState<{ top: number; left: number } | null>(null);

  const closeGroup = () => {
    setOpenGroup(null);
    setOpenGroupAnchor(null);
  };

  // Close on outside click, Escape, scroll, or resize. (Scroll/resize would
  // otherwise leave the fixed-position menu visually stranded.)
  useEffect(() => {
    if (!openGroup) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (
        !target?.closest(`[data-tab-group="${openGroup}"]`) &&
        !target?.closest('[data-tab-group-menu]')
      ) {
        closeGroup();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeGroup();
    };
    const onScrollOrResize = () => closeGroup();
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [openGroup]);
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-950/50 border border-red-800/50 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Admin Access Required</h2>
          <p className="text-gray-400 text-sm">
            You need to be a member of the <span className="text-violet-400 font-medium">SocialSpark-Admins</span> group to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-violet-950/20 to-gray-900 border-b border-gray-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 bg-violet-950/60 border border-violet-800/50 rounded-md text-xs font-medium text-violet-400">Admin</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
          <p className="text-gray-400 mt-1">Manage businesses, blog posts, home page content, and monitor infrastructure.</p>

          {/* Tabs */}
          <div className="flex overflow-x-auto gap-1 mt-8 border-b border-gray-800 -mx-4 px-4 sm:-mx-6 sm:px-6 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
            {TABS.map(tab => {
              if (isGroup(tab)) {
                const childActive = tab.children.some(c => c.id === activeTab);
                const isOpen = openGroup === tab.id;
                return (
                  <div key={tab.id} data-tab-group={tab.id}>
                    <button
                      onClick={(e) => {
                        if (isOpen) {
                          closeGroup();
                          return;
                        }
                        const r = e.currentTarget.getBoundingClientRect();
                        setOpenGroupAnchor({ top: r.bottom + 4, left: r.left });
                        setOpenGroup(tab.id);
                      }}
                      aria-haspopup="menu"
                      aria-expanded={isOpen}
                      className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all -mb-px border-b-2 whitespace-nowrap ${
                        childActive
                          ? 'text-violet-300 border-violet-500 bg-violet-950/20'
                          : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/50'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                      <svg
                        className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                    {isOpen && openGroupAnchor && (
                      <div
                        role="menu"
                        data-tab-group-menu
                        style={{
                          position: 'fixed',
                          top: openGroupAnchor.top,
                          left: openGroupAnchor.left,
                        }}
                        className="min-w-[260px] bg-gray-900 border border-gray-800 rounded-lg shadow-xl shadow-black/50 z-[1000] overflow-hidden"
                      >
                        {tab.children.map(child => {
                          const isActive = activeTab === child.id;
                          return (
                            <button
                              key={child.id}
                              role="menuitem"
                              onClick={() => {
                                setActiveTab(child.id);
                                closeGroup();
                              }}
                              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-left transition-colors ${
                                isActive
                                  ? 'text-violet-300 bg-violet-950/40'
                                  : 'text-gray-300 hover:text-gray-100 hover:bg-gray-800'
                              }`}
                            >
                              {child.icon}
                              {child.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all -mb-px border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-violet-300 border-violet-500 bg-violet-950/20'
                      : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/50'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {activeTab === 'create' && <CreateHub intent={createIntent ?? undefined} />}
        {activeTab === 'businesses' && (
          <BusinessesTab
            onRequestCreateDeal={(business, env) =>
              openCreate({ type: 'deal', env, business: { id: business.id, name: business.name, timezone: business.timezone } })
            }
          />
        )}
        {activeTab === 'events' && <EventsTab />}
        {activeTab === 'deals' && <DealsTab onRequestCreate={(env) => openCreate({ type: 'deal', env })} />}
        {activeTab === 'blog-posts' && <BlogPostsTab />}
        {activeTab === 'home-page' && <HomePageTab />}
        {activeTab === 'monitoring' && <MonitoringTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'notification-opens' && <NotificationOpensTab />}
        {activeTab === 'gamification' && <GamificationTab />}
        {activeTab === 'referral-codes' && <ReferralCodesTab />}
        {activeTab === 'business-recovery' && <BusinessRecoveryTab />}
        {activeTab === 'business-push-notifications' && <BusinessPushNotificationsTab />}
        {activeTab === 'area-notifications' && <AreaNotificationsPage />}
        {activeTab === 'venue-claims' && <VenueClaimsTab />}
        {activeTab === 'enterprise-pricing' && <EnterprisePricingTab />}
        {activeTab === 'qr-codes' && <QrCodesTab />}
      </div>
    </div>
  );
}

export default BusinessesPage;
